import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type {
  InsertInternalAuditInput,
  InsertInternalAuditMutation,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { InsertInternalAuditDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

/**
 * tRPC input type for insertInternalAudit. BusinessArea is free-text (the
 * data-layer resolves it to a BusinessAreaId).
 */
type InsertInternalAuditTRPCInput = {
  Title: string;
  Description?: string | null;
  BusinessArea?: string | null;
  CustomAttributeData?: CustomAttributeData | null;
  OwnerUserIds?: string[];
  OwnerGroupIds?: string[];
  ContributorUserIds?: string[];
  ContributorGroupIds?: string[];
  ViewerUserIds?: string[] | null;
  ViewerGroupIds?: string[] | null;
  TagTypeIds?: string[];
  DepartmentTypeIds?: string[];
};

export const useInsertInternalAudit = createMutationHook({
  name: 'insertInternalAudit',
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.internalAuditEntity.insert.mutationOptions({ onSuccess }),
  mapInput: (
    variables: InsertInternalAuditInput
  ): InsertInternalAuditTRPCInput => ({
    Title: variables.Title ?? '',
    Description: variables.Description,
    BusinessArea: variables.BusinessArea,
    CustomAttributeData: variables.CustomAttributeData,
    OwnerUserIds: variables.OwnerUserIds ?? [],
    OwnerGroupIds: variables.OwnerGroupIds ?? [],
    ContributorUserIds: variables.ContributorUserIds ?? [],
    ContributorGroupIds: variables.ContributorGroupIds ?? [],
    ViewerUserIds: variables.ViewerUserIds,
    ViewerGroupIds: variables.ViewerGroupIds,
    TagTypeIds: variables.TagTypeIds ?? [],
    DepartmentTypeIds: variables.DepartmentTypeIds ?? [],
  }),
  invalidate: (trpc) => [
    trpc.frontend.internalAuditEntity.register.queryKey(),
    trpc.frontend.internalAuditEntity.internalAuditById.queryKey(),
    // A new free-text BusinessArea find-or-creates a business_area row, so
    // refresh the business-area list (parity with the Apollo path's
    // evictField(cache, 'business_area')).
    trpc.frontend.businessArea.businessAreas.queryKey(),
  ],
  mapResponse: (result: { Id: string }): InsertInternalAuditMutation => ({
    insertInternalAudit: {
      __typename: 'IdOutput',
      Id: result.Id,
    },
  }),
  graphqlDocument: InsertInternalAuditDocument,
  graphqlVariables: (variables: InsertInternalAuditInput) => ({
    Input: variables,
  }),
  evictFields: [
    'internal_audit_entity',
    'internal_audit_entity_aggregate',
    'business_area',
  ],
});
